/**
 * プログレスバー表示コンポーネント
 * アップロード進捗を視覚的に表示
 */

class ProgressBarManager {
  constructor(containerId = 'upload-progress-container') {
    this.containerId = containerId;
    this.progressBars = new Map();
    this.init();
  }

  /**
   * 初期化
   */
  init() {
    // プログレスバーコンテナを作成
    if (!document.getElementById(this.containerId)) {
      const container = document.createElement('div');
      container.id = this.containerId;
      container.className = 'upload-progress-container';
      container.innerHTML = `
        <style>
          .upload-progress-container {
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 9999;
            max-width: 400px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }
          
          .upload-progress-item {
            background: #ffffff;
            border: 1px solid #e1e5e9;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 12px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            transition: all 0.3s ease;
          }
          
          .upload-progress-item.completed {
            background: #f0f9ff;
            border-color: #22c55e;
          }
          
          .upload-progress-item.error {
            background: #fef2f2;
            border-color: #ef4444;
          }
          
          .upload-progress-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
          }
          
          .upload-progress-title {
            font-weight: 600;
            color: #1f2937;
            font-size: 14px;
          }
          
          .upload-progress-close {
            background: none;
            border: none;
            font-size: 18px;
            cursor: pointer;
            color: #6b7280;
            padding: 0;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .upload-progress-close:hover {
            color: #374151;
          }
          
          .upload-progress-bar {
            width: 100%;
            height: 8px;
            background-color: #e5e7eb;
            border-radius: 4px;
            overflow: hidden;
            margin-bottom: 8px;
          }
          
          .upload-progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #3b82f6, #1d4ed8);
            border-radius: 4px;
            transition: width 0.3s ease;
            position: relative;
          }
          
          .upload-progress-fill.completed {
            background: linear-gradient(90deg, #22c55e, #16a34a);
          }
          
          .upload-progress-fill.error {
            background: linear-gradient(90deg, #ef4444, #dc2626);
          }
          
          .upload-progress-message {
            font-size: 12px;
            color: #6b7280;
            line-height: 1.4;
          }
          
          .upload-progress-stats {
            font-size: 11px;
            color: #9ca3af;
            margin-top: 4px;
          }
          
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes slideOutRight {
            from {
              transform: translateX(0);
              opacity: 1;
            }
            to {
              transform: translateX(100%);
              opacity: 0;
            }
          }
          
          .upload-progress-item.entering {
            animation: slideInRight 0.3s ease;
          }
          
          .upload-progress-item.leaving {
            animation: slideOutRight 0.3s ease;
          }
        </style>
      `;
      document.body.appendChild(container);
    }
  }

  /**
   * 新しいプログレスバーを作成
   */
  create(id, title = 'ファイルをアップロード中') {
    const container = document.getElementById(this.containerId);
    
    const progressItem = document.createElement('div');
    progressItem.className = 'upload-progress-item entering';
    progressItem.id = `progress-${id}`;
    
    progressItem.innerHTML = `
      <div class="upload-progress-header">
        <span class="upload-progress-title">${title}</span>
        <button class="upload-progress-close" onclick="window.progressBarManager.remove('${id}')">&times;</button>
      </div>
      <div class="upload-progress-bar">
        <div class="upload-progress-fill" style="width: 0%"></div>
      </div>
      <div class="upload-progress-message">準備中...</div>
      <div class="upload-progress-stats">0 / 0 ファイル</div>
    `;
    
    container.appendChild(progressItem);
    
    const progressBar = {
      element: progressItem,
      fillElement: progressItem.querySelector('.upload-progress-fill'),
      messageElement: progressItem.querySelector('.upload-progress-message'),
      statsElement: progressItem.querySelector('.upload-progress-stats')
    };
    
    this.progressBars.set(id, progressBar);
    return progressBar;
  }

  /**
   * プログレス更新
   */
  update(id, data) {
    const progressBar = this.progressBars.get(id);
    if (!progressBar) return;

    const { percentage, message, completed, total, status } = data;

    // プログレスバーの更新
    if (typeof percentage === 'number') {
      progressBar.fillElement.style.width = `${Math.min(100, Math.max(0, percentage))}%`;
    }

    // メッセージの更新
    if (message) {
      progressBar.messageElement.textContent = message;
    }

    // 統計情報の更新
    if (typeof completed === 'number' && typeof total === 'number') {
      progressBar.statsElement.textContent = `${completed} / ${total} ファイル`;
    }

    // ステータスに応じたスタイル更新
    if (status === 'completed') {
      progressBar.element.classList.add('completed');
      progressBar.fillElement.classList.add('completed');
      progressBar.fillElement.style.width = '100%';
      progressBar.messageElement.textContent = message || 'アップロード完了！';
      
      // 3秒後に自動削除
      setTimeout(() => this.remove(id), 3000);
    } else if (status === 'error') {
      progressBar.element.classList.add('error');
      progressBar.fillElement.classList.add('error');
      progressBar.messageElement.textContent = message || 'エラーが発生しました';
    }
  }

  /**
   * プログレスバーを削除
   */
  remove(id) {
    const progressBar = this.progressBars.get(id);
    if (!progressBar) return;

    progressBar.element.classList.add('leaving');
    
    setTimeout(() => {
      progressBar.element.remove();
      this.progressBars.delete(id);
    }, 300);
  }

  /**
   * 全てのプログレスバーを削除
   */
  removeAll() {
    this.progressBars.forEach((_, id) => {
      this.remove(id);
    });
  }
}

// シングルトンインスタンスを作成
window.progressBarManager = new ProgressBarManager();

export default ProgressBarManager;